import React, { createContext, useState, useContext, useEffect } from 'react';
import { getCurrentUser, api } from '../services/auth';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const [subscription, setSubscription] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load subscription data from current user
  useEffect(() => {
    loadSubscriptionData();
  }, []);

  const loadSubscriptionData = () => {
    const user = getCurrentUser();
    if (user) {
      setSubscription(user.subscription || null);
      setModules(user.modules || []);
    } else {
      setSubscription(null);
      setModules([]);
    }
    setLoading(false);
    // Always revalidate against the backend to reflect any admin-side changes
    // (e.g., subscription updated from back office)
    void revalidateFromServer();
  };

  // Ask backend for authoritative subscription state
  const revalidateFromServer = async () => {
    try {
      // 1) Get authoritative subscription name and modules for current user
      const subResp = await api.get('/auth/me/subscription', { withCredentials: true });
      const serverSubscription = subResp?.data?.subscription || null;
      const serverModules = Array.isArray(subResp?.data?.modules) ? subResp.data.modules : [];

      setSubscription((prev) => {
        const next = serverSubscription;
        // Keep localStorage in sync when it changes
        const current = getCurrentUser();
        if (current && (prev !== next || JSON.stringify(current.modules||[]) !== JSON.stringify(serverModules))) {
          const updated = { ...current, subscription: next, modules: serverModules };
          localStorage.setItem('user', JSON.stringify(updated));
        }
        return next;
      });
      setModules(serverModules);

      // 2) Optional: refresh billing card data (non-critical)
      try {
        await api.get('/billing/current', { withCredentials: true });
      } catch (_) { /* ignore */ }
    } catch (err) {
      // Silently ignore; UI can continue using last known subscription
    }
  };

  // Refresh on window focus and at a light interval to make updates feel immediate
  useEffect(() => {
    const onFocus = () => { void revalidateFromServer(); };
    window.addEventListener('focus', onFocus);
    const interval = setInterval(() => { void revalidateFromServer(); }, 30000);
    return () => {
      window.removeEventListener('focus', onFocus);
      clearInterval(interval);
    };
  }, []);

  // Check if user has access to a specific module
  const hasModule = (moduleCode) => {
    if (!moduleCode) return true;
    return modules.includes(moduleCode);
  };

  // Check if user has any of the specified modules
  const hasAnyModule = (moduleCodes) => {
    if (!moduleCodes || moduleCodes.length === 0) return true;
    return moduleCodes.some(code => modules.includes(code));
  };

  // Check if user has all of the specified modules
  const hasAllModules = (moduleCodes) => {
    if (!moduleCodes || moduleCodes.length === 0) return true;
    return moduleCodes.every(code => modules.includes(code));
  };

  // Get subscription tier level (for comparison)
  const getSubscriptionLevel = () => {
    const levels = {
      'Demo': 1,
      'Growth': 2,
      'Pro': 3,
      'Enterprise': 4
    };
    return levels[subscription] || 0;
  };

  // Check if user needs to upgrade for a feature
  const needsUpgrade = (requiredModule) => {
    return !hasModule(requiredModule);
  };

  // Get recommended plan for a module
  const getRecommendedPlan = (moduleCode) => {
    // Mapping of modules to plans (simplified)
    const moduleToPlans = {
      'basic_ai_chat': 'Pro',
      'advanced_dashboards': 'Pro',
      'loyalty_program': 'Pro',
      'multi_brand': 'Enterprise',
      'custom_api_integrations': 'Enterprise',
      'support_dedicated': 'Enterprise',
      'account_manager': 'Enterprise',
      'auto_reconciliation': 'Growth',
      'visitor_tracking': 'Growth',
      'ai_segment_analysis': 'Growth'
    };
    return moduleToPlans[moduleCode] || 'Pro';
  };

  const value = {
    subscription,
    modules,
    loading,
    hasModule,
    hasAnyModule,
    hasAllModules,
    getSubscriptionLevel,
    needsUpgrade,
    getRecommendedPlan,
    refreshSubscription: revalidateFromServer
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

